"use client";

import { AnimatePresence } from "motion/react";
import SearchBar from "@/components/features/search/SearchBar";
import { ArrowRight, Star, Shield, Users, ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { useClientMotion } from '@/lib/hooks/use-reduced-motion-ssr';

const HeroSection = () => {
  const { MotionDiv, MotionH1, MotionP, MotionImg, motionProps } = useClientMotion();
  
  // Search functionality is now handled internally by SearchBar component

  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: "/images/modern-apartment.jpg",
      review: {
        title: "Amazing Location",
        rating: 5,
      }
    },
    {
      image: "/images/apartment-interior.jpg",
      review: {
        title: "Great Value",
        rating: 5,
      }
    },
    {
      image: "/images/luxury-condo.jpg",
      review: {
        title: "Excellent Service",
        rating: 5,
      }
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const stats = [
    { icon: Users, value: "50K+", label: "Active Users" },
    { icon: Star, value: "100K+", label: "Reviews" },
    { icon: Shield, value: "99%", label: "Verified" }
  ];

  return (
    <div className="relative min-h-[85vh] bg-linear-to-br from-blue-50 via-white to-blue-100 overflow-hidden">
      <MotionDiv
        {...motionProps({
          initial: { opacity: 0, scale: 0.8 },
          animate: { opacity: 0.1, scale: 1 },
          transition: { duration: 2, repeat: Infinity, repeatType: "reverse" }
        })}
        className="absolute top-40 -right-20 w-96 h-96 bg-blue-300 rounded-full blur-3xl"
      />
      <MotionDiv
        {...motionProps({
          initial: { opacity: 0, scale: 0.8 },
          animate: { opacity: 0.1, scale: 1 },
          transition: { duration: 2, repeat: Infinity, repeatType: "reverse", delay: 1 }
        })}
        className="absolute -bottom-40 -left-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl"
      />

      <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <MotionDiv
            {...motionProps({
              initial: "hidden",
              animate: "visible",
              variants: staggerContainer
            })}
            className="max-w-2xl"
          >
            <MotionH1 
              {...motionProps({ variants: fadeInUp })}
              className="text-5xl md:text-7xl font-bold mb-6 bg-linear-to-r from-gray-900 via-blue-800 to-blue-900 bg-clip-text text-transparent"
            >
              Know Before You Rent.
            </MotionH1>
            
            <MotionP 
              {...motionProps({ variants: fadeInUp })}
              className="text-xl text-gray-700 mb-2"
            >
              Verified reviews from real tenants, search any address.
            </MotionP>
            
            <MotionP 
              {...motionProps({ variants: fadeInUp })}
              className="text-xl text-gray-700 mb-8"
            >
              Make smarter rental decisions with RentWise.
            </MotionP>

            <MotionDiv 
              {...motionProps({ variants: fadeInUp })}
              className="w-full max-w-xl mb-12"
            >
              <SearchBar />
            </MotionDiv>

            {/* Stats Section */}
            <MotionDiv
              {...motionProps({ variants: staggerContainer })}
              className="grid grid-cols-3 gap-6"
            >
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <MotionDiv
                    key={index}
                    {...motionProps({ variants: fadeInUp })}
                    className="text-center group"
                  >
                    <div className="flex items-center justify-center mb-2">
                      <IconComponent className="h-5 w-5 text-blue-600 mr-2" />
                      <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
                    </div>
                    <span className="text-sm text-gray-600">{stat.label}</span>
                  </MotionDiv>
                );
              })}
            </MotionDiv>
          </MotionDiv>

          {/* Right Column - Image Carousel */}
          <MotionDiv
            {...motionProps({
              initial: { opacity: 0, x: 50 },
              animate: { opacity: 1, x: 0 },
              transition: { duration: 0.8, delay: 0.5 }
            })}
            className="hidden lg:block relative"
          >
            <div className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl transform-gpu rotate-2 hover:rotate-0 transition-transform duration-500">
              <AnimatePresence mode="wait">
                <MotionImg
                  key={currentSlide}
                  src={slides[currentSlide].image}
                  alt="Featured Property"
                  {...motionProps({
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    exit: { opacity: 0 },
                    transition: { duration: 0.5 }
                  })}
                  className="object-cover w-full h-full"
                />
              </AnimatePresence>

              {/* Navigation Arrows */}
              <div className="absolute inset-0 flex items-center justify-between p-4">
                <button
                  onClick={prevSlide}
                  className="p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-lg hover:bg-white transition-colors"
                >
                  <ChevronLeft className="h-6 w-6 text-gray-800" />
                </button>
                <button
                  onClick={nextSlide}
                  className="p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-lg hover:bg-white transition-colors"
                >
                  <ChevronRight className="h-6 w-6 text-gray-800" />
                </button>
              </div>

              <MotionDiv
                {...motionProps({
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { delay: 1 }
                })}
                className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{slides[currentSlide].review.title}</h3>
                    <div className="flex items-center gap-1 text-yellow-500 mt-1">
                      {[...Array(slides[currentSlide].review.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-current" />
                      ))}
                    </div>
                  </div>
                  <Link 
                    href="/reviews"
                    className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm font-medium"
                  >
                    View More
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </MotionDiv>

              {/* Slide Indicators */}
              <div className="absolute bottom-24 left-0 right-0 flex justify-center gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      currentSlide === index 
                        ? 'bg-blue-600 w-4' 
                        : 'bg-white/60 hover:bg-white'
                    }`}
                  />
                ))}
              </div>
            </div>
          </MotionDiv>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
